import { useState, useEffect } from "react";
import { CheckCircle, Package, CreditCard, Loader2, Copy, Check, Building, Zap, Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import API from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { CUSTOMER } from "@/constants/testIds";
import { useAuth } from "@/contexts/AuthContext";

const METHODS = [
  { id: "bank_transfer", label: "Bank Transfer", icon: Building, desc: "Transfer via bank, admin approves within 1–24 hrs" },
  { id: "paystack", label: "Pay with Paystack", icon: CreditCard, desc: "Instant — Debit/Credit card, Bank, USSD" },
  { id: "flutterwave", label: "Pay with Flutterwave", icon: Wallet, desc: "Instant — Card, Bank Transfer, Mobile Money" },
];

export default function BuyCredit() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [packages, setPackages] = useState([]);
  const [bankDetails, setBankDetails] = useState(null);
  const [gatewaySettings, setGatewaySettings] = useState({ paystack: { enabled: false } });
  const [selected, setSelected] = useState(null);
  const [method, setMethod] = useState("bank_transfer");
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1); // 1=package, 2=method, 3=bank details/pay
  const [form, setForm] = useState({ sender_name: "", transfer_reference: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [pkgs, bd, gs] = await Promise.all([
        API.get("/payments/packages"),
        API.get("/payments/bank-details"),
        API.get("/payments/gateway-settings/public").catch(() => ({ data: {} })),
      ]);
      setPackages(pkgs.data.packages?.filter((p) => p.status === "active") || []);
      setBankDetails(bd.data);
      if (gs.data) setGatewaySettings(gs.data);
    } catch { toast.error("Failed to load packages"); }
    finally { setLoading(false); }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };

  const submitBankTransfer = async () => {
    if (!selected || !form.transfer_reference) { toast.error("Fill in transfer reference"); return; }
    setSubmitting(true);
    try {
      await API.post("/payments/bank-transfer", {
        package_id: selected._id,
        ...form,
        amount_transferred: selected.price,
      });
      toast.success("Transfer submitted! Credits will be added after verification.");
      navigate("/dashboard/wallet");
    } catch (e) { toast.error(e?.response?.data?.detail || "Submission failed"); }
    finally { setSubmitting(false); }
  };

  const payWithPaystack = async () => {
    if (!selected) { toast.error("Select a package first"); return; }
    setSubmitting(true);
    try {
      const callbackUrl = `${window.location.origin}/payment/callback`;
      const { data } = await API.post("/payments/paystack/initialize", {
        package_id: selected._id,
        callback_url: callbackUrl,
      });
      window.location.href = data.authorization_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to initialize Paystack payment");
      setSubmitting(false);
    }
  };

  const payWithFlutterwave = async () => {
    if (!selected) { toast.error("Select a package first"); return; }
    setSubmitting(true);
    try {
      const redirectUrl = `${window.location.origin}/payment/callback`;
      const { data } = await API.post("/payments/flutterwave/initialize", {
        package_id: selected._id,
        redirect_url: redirectUrl,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to initialize Flutterwave payment");
      setSubmitting(false);
    }
  };

  const availableMethods = METHODS.filter((m) => {
    if (m.id === "paystack") return gatewaySettings?.paystack?.enabled;
    if (m.id === "flutterwave") return gatewaySettings?.flutterwave?.enabled;
    return true;
  });

  if (loading) return (
    <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-28 bg-slate-100 rounded-xl animate-pulse" />)}</div>
  );

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="text-2xl font-extrabold text-slate-900">Buy Credits</h1>
        <p className="text-sm text-slate-500 mt-1">Select a package and complete payment</p>
      </div>

      {/* Progress */}
      <div className="flex items-center gap-3">
        {[["1", "Select Package"], ["2", "Payment Method"], ["3", "Complete"]].map(([n, l], i) => (
          <div key={n} className="flex items-center gap-2">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 transition-all ${
              step > parseInt(n) ? "bg-emerald-600 text-white" : step === parseInt(n) ? "bg-[#0c1e41] text-white shadow-md shadow-blue-600/25" : "bg-slate-100 text-slate-400"
            }`}>
              {step > parseInt(n) ? <Check size={12} /> : n}
            </div>
            <span className={`text-xs font-medium hidden sm:block ${step === parseInt(n) ? "text-slate-900" : "text-slate-400"}`}>{l}</span>
            {i < 2 && <div className="w-6 sm:w-10 h-0.5 bg-slate-200 mx-1" />}
          </div>
        ))}
      </div>

      {/* Step 1: Choose package */}
      {step === 1 && (
        <div className="space-y-4">
          <h2 className="text-base font-bold text-slate-900">Choose a Credit Package</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid={CUSTOMER.creditPackages}>
            {packages.map((pkg) => (
              <div key={pkg._id}
                onClick={() => setSelected(pkg)}
                className={`cursor-pointer border-2 rounded-xl p-5 transition-all ${
                  selected?._id === pkg._id
                    ? "border-[#0c1e41] bg-[#eef1f8] shadow-md"
                    : "border-slate-200 bg-white hover:border-[#0c1e41]/40 shadow-sm hover:shadow-md"
                }`}
                data-testid={CUSTOMER.creditPackageCard}
              >
                {pkg.featured && <Badge className="mb-3 bg-[#0c1e41] text-white text-xs">Most Popular</Badge>}
                <div className="text-base font-extrabold text-slate-900 mb-1">{pkg.name}</div>
                <div className="text-2xl font-extrabold font-mono text-[#0c1e41] mb-1">₦{((pkg.price || 0) / 100).toLocaleString()}</div>
                <div className="text-sm text-slate-700 font-semibold">{pkg.credits.toLocaleString()} credits</div>
                {pkg.bonus_credits > 0 && <div className="text-xs text-emerald-700 font-semibold mt-1">+{pkg.bonus_credits} bonus credits free!</div>}
                {selected?._id === pkg._id && (
                  <div className="mt-3 flex items-center gap-1.5 text-xs text-[#0c1e41] font-semibold">
                    <CheckCircle size={13} /> Selected
                  </div>
                )}
              </div>
            ))}
          </div>
          <Button
            onClick={() => selected ? setStep(2) : toast.error("Select a package first")}
            className="bg-[#0c1e41] hover:bg-[#091629] mt-2 shadow-sm"
          >
            Continue <Check size={15} className="ml-2" />
          </Button>
        </div>
      )}

      {/* Step 2: Choose method */}
      {step === 2 && selected && (
        <div className="space-y-4">
          <h2 className="text-base font-bold text-slate-900">
            Pay ₦{((selected.price || 0) / 100).toLocaleString()} for {(selected.credits + (selected.bonus_credits || 0)).toLocaleString()} credits
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {availableMethods.map((m) => (
              <div
                key={m.id}
                onClick={() => setMethod(m.id)}
                className={`cursor-pointer border-2 rounded-xl p-5 transition-all ${
                  method === m.id
                    ? "border-[#0c1e41] bg-[#eef1f8] shadow-md"
                    : "border-slate-200 bg-white hover:border-[#0c1e41]/40 shadow-sm"
                }`}
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${method === m.id ? "bg-[#0c1e41]" : "bg-slate-100"}`}>
                    <m.icon size={18} className={method === m.id ? "text-white" : "text-slate-600"} />
                  </div>
                  <span className="text-sm font-bold text-slate-900">{m.label}</span>
                </div>
                <p className="text-xs text-slate-500">{m.desc}</p>
                {method === m.id && (
                  <div className="mt-2 flex items-center gap-1 text-xs text-[#0c1e41] font-semibold">
                    <CheckCircle size={12} /> Selected
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setStep(1)} className="border-slate-300 text-slate-700 hover:bg-slate-50">Back</Button>
            <Button onClick={() => setStep(3)} className="bg-[#0c1e41] hover:bg-[#091629] shadow-sm">
              Continue
            </Button>
          </div>
        </div>
      )}

      {/* Step 3: Bank transfer details */}
      {step === 3 && selected && method === "bank_transfer" && (
        <div className="space-y-4">
          <h2 className="text-base font-bold text-slate-900">Transfer ₦{((selected.price || 0) / 100).toLocaleString()} to:</h2>
          <div className="bg-[#eef1f8] border border-[#c0cfe8] rounded-xl p-5 space-y-3">
            {bankDetails && (bankDetails.account_number || bankDetails.bank_name) ? (
              <>
                {[["Bank", bankDetails.bank_name], ["Account Name", bankDetails.account_name], ["Account Number", bankDetails.account_number]].map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between">
                    <span className="text-sm text-[#0c1e41]">{k}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-slate-900">{v}</span>
                      {k === "Account Number" && v && (
                        <button onClick={() => copy(v)} className="p-1 text-[#0c1e41] hover:text-[#0c1e41]">
                          {copied ? <Check size={13} /> : <Copy size={13} />}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                {bankDetails.instructions && (
                  <div className="text-xs text-[#0c1e41] bg-[#dce4f0]/50 border border-[#c0cfe8] rounded-lg p-3 mt-2">{bankDetails.instructions}</div>
                )}
              </>
            ) : (
              <p className="text-sm text-amber-700">Bank details not configured yet. Contact support or choose another payment method.</p>
            )}
          </div>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-700 text-sm">Sender Name *</Label>
                <Input value={form.sender_name} onChange={(e) => setForm({...form,sender_name:e.target.value})} placeholder="Name on bank account" className="bg-white border-slate-300 text-slate-900 h-10 placeholder:text-slate-400" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700 text-sm">Transfer Reference *</Label>
                <Input value={form.transfer_reference} onChange={(e) => setForm({...form,transfer_reference:e.target.value})} placeholder="Transaction ID / narration" className="bg-white border-slate-300 text-slate-900 h-10 placeholder:text-slate-400" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700 text-sm">Additional Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({...form,notes:e.target.value})} placeholder="Any additional info..." rows={2} className="bg-white border-slate-300 text-slate-900 resize-none" />
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setStep(2)} className="border-slate-300 text-slate-700 hover:bg-slate-50">Back</Button>
              <Button onClick={submitBankTransfer} disabled={submitting} className="bg-[#0c1e41] hover:bg-[#091629] flex-1 shadow-sm">
                {submitting ? <><Loader2 size={14} className="animate-spin mr-2" />Submitting...</> : "Submit Transfer Details"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Step 3: Paystack */}
      {step === 3 && selected && method === "paystack" && (
        <div className="space-y-4">
          <h2 className="text-base font-bold text-slate-900">Pay with Paystack</h2>
          <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm text-center">
            <div className="w-14 h-14 bg-[#eef1f8] border border-[#c0cfe8] rounded-2xl flex items-center justify-center mx-auto mb-4">
              <CreditCard size={24} className="text-[#0c1e41]" />
            </div>
            <h3 className="text-base font-bold text-slate-900 mb-1">{selected.name} Package</h3>
            <div className="text-3xl font-extrabold font-mono text-[#0c1e41] mb-1">₦{((selected.price || 0) / 100).toLocaleString()}</div>
            <p className="text-sm text-slate-500 mb-1">
              {selected.credits.toLocaleString()} credits
              {selected.bonus_credits > 0 && <span className="text-emerald-600 font-semibold"> + {selected.bonus_credits} bonus</span>}
            </p>
            <p className="text-xs text-slate-400 mb-6">You'll be redirected to Paystack to complete your payment securely.</p>
            <div className="flex flex-col gap-2">
              <Button onClick={payWithPaystack} disabled={submitting} className="w-full bg-[#0c1e41] hover:bg-[#091629] text-white h-11 font-semibold shadow-sm">
                {submitting
                  ? <><Loader2 size={15} className="animate-spin mr-2" />Redirecting to Paystack...</>
                  : <><Zap size={15} className="mr-2" />Pay ₦{((selected.price || 0) / 100).toLocaleString()} with Paystack</>
                }
              </Button>
              <p className="text-xs text-slate-400 text-center">Secured by Paystack · Card, Bank Transfer, USSD</p>
            </div>
          </div>
          <Button variant="outline" onClick={() => setStep(2)} className="border-slate-300 text-slate-700 hover:bg-slate-50">
            Back to Payment Methods
          </Button>
        </div>
      )}

      {/* Step 3: Flutterwave */}
      {step === 3 && selected && method === "flutterwave" && (
        <div className="space-y-4">
          <h2 className="text-base font-bold text-slate-900">Pay with Flutterwave</h2>
          <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm text-center">
            <div className="w-14 h-14 bg-[#eef1f8] border border-[#c0cfe8] rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Wallet size={24} className="text-[#0c1e41]" />
            </div>
            <h3 className="text-base font-bold text-slate-900 mb-1">{selected.name} Package</h3>
            <div className="text-3xl font-extrabold font-mono text-[#0c1e41] mb-1">₦{((selected.price || 0) / 100).toLocaleString()}</div>
            <p className="text-sm text-slate-500 mb-1">
              {selected.credits.toLocaleString()} credits
              {selected.bonus_credits > 0 && <span className="text-emerald-600 font-semibold"> + {selected.bonus_credits} bonus</span>}
            </p>
            <p className="text-xs text-slate-400 mb-6">You'll be redirected to Flutterwave to complete your payment securely.</p>
            <div className="flex flex-col gap-2">
              <Button onClick={payWithFlutterwave} disabled={submitting} className="w-full bg-[#0c1e41] hover:bg-[#091629] text-white h-11 font-semibold shadow-sm">
                {submitting
                  ? <><Loader2 size={15} className="animate-spin mr-2" />Redirecting to Flutterwave...</>
                  : <><Zap size={15} className="mr-2" />Pay ₦{((selected.price || 0) / 100).toLocaleString()} with Flutterwave</>
                }
              </Button>
              <p className="text-xs text-slate-400 text-center">Secured by Flutterwave · Card, Bank, Mobile Money</p>
            </div>
          </div>
          <Button variant="outline" onClick={() => setStep(2)} className="border-slate-300 text-slate-700 hover:bg-slate-50">
            Back to Payment Methods
          </Button>
        </div>
      )}
    </div>
  );
}
